import React, { useCallback, useEffect, useMemo, useState } from "react";

import {
  Box,
  InputBase,
  IconButton,
  Skeleton,
  Checkbox,
  Button,
  Typography,
  Paper,
  Chip,
  Stack,
  Tooltip,
  alpha,
  useTheme,
  useMediaQuery,
} from "@mui/material";

import { DataGrid } from "@mui/x-data-grid";

import SearchRoundedIcon from "@mui/icons-material/SearchRounded";
import StarRoundedIcon from "@mui/icons-material/StarRounded";
import SaveRoundedIcon from "@mui/icons-material/SaveRounded";
import RefreshRoundedIcon from "@mui/icons-material/RefreshRounded";
import Inventory2OutlinedIcon from "@mui/icons-material/Inventory2Outlined";
import TrendingUpRoundedIcon from "@mui/icons-material/TrendingUpRounded";
import CollectionsOutlinedIcon from "@mui/icons-material/CollectionsOutlined";

import Swal from "sweetalert2";
import config from "../../Config";
import productService from "../../repository/productRepository";

const TopProduct = () => {
  const theme = useTheme();
  const isDark = theme.palette.mode === "dark";
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));

  const [rows, setRows] = useState([]);
  const [searchQuery, setSearchQuery] = useState("");
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [saving, setSaving] = useState(false);
  const [changedRows, setChangedRows] = useState([]);

  // ---------------------------------------------------------------------------
  // COLORS
  // ---------------------------------------------------------------------------

  const colors = {
    background: isDark ? "#11100F" : "#F6F3EF",
    card: isDark ? "#191715" : "#FFFFFF",
    cardAlt: isDark ? "#151412" : "#FCFBF9",

    text: isDark ? "#F4F0EA" : "#27231F",
    secondary: isDark ? "#AAA49C" : "#77716A",

    accent: isDark ? "#D1B99A" : "#806B52",

    accentSoft: isDark ? "rgba(209,185,154,.10)" : "rgba(128,107,82,.08)",

    border: isDark ? "rgba(255,255,255,.08)" : "rgba(50,40,30,.09)",

    success: isDark ? "#8FC6A3" : "#3D8054",

    danger: isDark ? "#D99494" : "#B54545",
  };

  // ---------------------------------------------------------------------------
  // CARD STYLE
  // ---------------------------------------------------------------------------

  const cardSx = {
    borderRadius: "14px",
    backgroundColor: colors.card,
    border: `1px solid ${colors.border}`,
    boxShadow: isDark
      ? "0 8px 30px rgba(0,0,0,.14)"
      : "0 8px 30px rgba(50,40,30,.045)",
  };

  // ---------------------------------------------------------------------------
  // FETCH PRODUCTS
  // ---------------------------------------------------------------------------

  const fetchData = useCallback(
    async (showRefresh = false) => {
      try {
        if (showRefresh) {
          setRefreshing(true);
        } else {
          setLoading(true);
        }

        const response = await productService.getTopProducts();
        if (!response.success) {
          throw new Error("Failed to fetch products");
        }

        const products = response.data?.Products;
        const formattedRows = Array.isArray(products)
          ? products.map((item) => ({
              id: item?.productId || item._id,
              productId: item.productId || "",
              name: item?.name || "-",
              category: item?.category || "-",
              subCategory: item?.subCategory || "-",
              price: item?.rentAmount ?? 0,
              totalBookings: item.totalBookings ?? 0,
              totalRevenue: item.totalRevenue ?? 0,
              productStatus: item.productStatus,
              isTop: Boolean(item.isTop),
            }))
          : [];

        setRows(formattedRows);
        setChangedRows([]);
      } catch (error) {
        console.error("Failed to fetch top products:", error);

        await Swal.fire({
          title: "Unable to load products",
          text: "Please try again.",
          icon: "error",
          confirmButtonColor: colors.accent,
          background: colors.card,
          color: colors.text,
        });
      } finally {
        setLoading(false);
        setRefreshing(false);
      }
    },
    [colors.accent, colors.card, colors.text],
  );

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  // ---------------------------------------------------------------------------
  // SEARCH
  // ---------------------------------------------------------------------------

  const filteredRows = useMemo(() => {
    const query = searchQuery.trim().toLowerCase();

    if (!query) {
      return rows;
    }

    return rows.filter((row) =>
      [row.name, row.productId, row.category, row.brand].some((value) =>
        String(value || "")
          .toLowerCase()
          .includes(query),
      ),
    );
  }, [rows, searchQuery]);

  // ---------------------------------------------------------------------------
  // COUNTS
  // ---------------------------------------------------------------------------

  const topCount = useMemo(
    () => rows.filter((row) => row.isTop).length,
    [rows],
  );

  const pendingCount = changedRows.length;

  // ---------------------------------------------------------------------------
  // HANDLE FEATURED CHANGE
  // ---------------------------------------------------------------------------

  const handleCheckboxChange = (productId, checked) => {
    const originalRow = rows.find((row) => row.productId === productId);

    if (!originalRow) {
      return;
    }

    setRows((prev) =>
      prev.map((row) =>
        row.productId === productId
          ? {
              ...row,
              isTop: checked,
            }
          : row,
      ),
    );

    setChangedRows((prev) => {
      const existing = prev.find((item) => item.productId === productId);

      const filtered = prev.filter((item) => item.productId !== productId);

      // If the user returned to the original state,
      // remove it from pending changes.
      if (originalRow.isTop === checked) {
        return filtered;
      }

      // Keep the latest selected value.
      if (existing) {
        return [
          ...filtered,
          {
            productId,
            isTop: checked,
          },
        ];
      }

      return [
        ...filtered,
        {
          productId,
          isTop: checked,
        },
      ];
    });
  };

  // ---------------------------------------------------------------------------
  // SAVE CHANGES
  // ---------------------------------------------------------------------------

  const handleSubmitChanges = async () => {
    if (!changedRows.length) {
      return;
    }

    try {
      setSaving(true);

      const token = sessionStorage.getItem("adminToken");

      // const response = await fetch(
      //   `${config.apiBaseUrl}/admin/setTopProducts`,
      //   {
      //     method: "POST",
      //     headers: {
      //       "x-access-token": token,
      //       "Content-Type": "application/json",
      //     },
      //     body: JSON.stringify({
      //       updatedProducts: changedRows,
      //     }),
      //   },
      // );
      const response = await productService.setTopProducts(changedRows, token);

      if (response.status === 200) {
        await Swal.fire({
          title: "Changes Saved",
          text: "Featured products have been updated successfully.",
          icon: "success",
          confirmButtonText: "Done",
          confirmButtonColor: colors.accent,
          background: colors.card,
          color: colors.text,
        });

        await fetchData(true);
      } else {
        const errorData = await response.json();

        await Swal.fire({
          title: "Update Failed",
          text: errorData?.message || "Something went wrong.",
          icon: "error",
          confirmButtonColor: colors.accent,
          background: colors.card,
          color: colors.text,
        });
      }
    } catch (error) {
      console.error("Failed to save top products:", error);

      await Swal.fire({
        title: "Update Failed",
        text: "Network error occurred.",
        icon: "error",
        confirmButtonColor: colors.accent,
        background: colors.card,
        color: colors.text,
      });
    } finally {
      setSaving(false);
    }
  };

  // ---------------------------------------------------------------------------
  // DATA GRID COLUMNS
  // ---------------------------------------------------------------------------

  const columns = [
    {
      field: "name",
      headerName: "PRODUCT",
      flex: 1.5,
      minWidth: 200,
    },
    {
      field: "category",
      headerName: "CATEGORY",
      flex: 1,
      minWidth: 130,
    },
    {
      field: "subCategory",
      headerName: "SUBCATEGORY",
      flex: 1,
      minWidth: 130,
    },
    {
      field: "price",
      headerName: "RENT AMOUNT",
      flex: 0.9,
      minWidth: 120,
      renderCell: (params) => (
        <Typography
          sx={{
            fontSize: 12.5,
            color: colors.text,
            fontWeight: 650,
          }}
        >
          ₹{Number(params.value ?? 0).toLocaleString("en-IN")}
        </Typography>
      ),
    },
    {
      field: "totalBookings",
      headerName: "BOOKINGS",
      flex: 0.8,
      minWidth: 100,
    },
    {
      field: "totalRevenue",
      headerName: "REVENUE",
      flex: 1,
      minWidth: 120,
      renderCell: (params) => (
        <Typography
          sx={{
            fontSize: 12.5,
            color: colors.text,
            fontWeight: 650,
          }}
        >
          ₹{Number(params.value ?? 0).toLocaleString("en-IN")}
        </Typography>
      ),
    },
    {
      field: "productStatus",
      headerName: "STATUS",
      flex: 0.8,
      minWidth: 100,
      renderCell: (params) => (
        <Chip
          label={params.value ? "Active" : "Inactive"}
          size="small"
          color={params.value ? "success" : "default"}
        />
      ),
    },

    // Keep your existing isTop column and its Checkbox renderCell here.
  ];
  // ---------------------------------------------------------------------------
  // SKELETON
  // ---------------------------------------------------------------------------

  const renderSkeleton = () => (
    <Stack
      spacing={1}
      sx={{
        p: 2,
      }}
    >
      {Array.from({ length: 7 }).map((_, index) => (
        <Skeleton
          key={index}
          variant="rounded"
          height={54}
          animation="wave"
          sx={{
            borderRadius: "8px",
            backgroundColor: isDark
              ? "rgba(255,255,255,.06)"
              : "rgba(50,40,30,.06)",
          }}
        />
      ))}
    </Stack>
  );

  // ---------------------------------------------------------------------------
  // UI
  // ---------------------------------------------------------------------------

  return (
    <Box
      sx={{
        width: "100%",
        minHeight: "100%",
        backgroundColor: colors.background,
        color: colors.text,
        p: {
          xs: 1.5,
          sm: 2.5,
          md: 4,
        },
        boxSizing: "border-box",
      }}
    >
      <Box
        sx={{
          maxWidth: 1500,
          mx: "auto",
        }}
      >
        {/* ----------------------------------------------------------------- */}
        {/* HEADER */}
        {/* ----------------------------------------------------------------- */}

        <Box
          sx={{
            display: "flex",
            alignItems: {
              xs: "flex-start",
              sm: "center",
            },
            justifyContent: "space-between",
            gap: 2,
            mb: 3,
            flexDirection: {
              xs: "column",
              sm: "row",
            },
          }}
        >
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              gap: 1.5,
            }}
          >
            <Box
              sx={{
                width: 46,
                height: 46,
                borderRadius: "11px",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                backgroundColor: colors.accentSoft,
                color: colors.accent,
                flexShrink: 0,
              }}
            >
              <StarRoundedIcon
                sx={{
                  fontSize: 25,
                }}
              />
            </Box>

            <Box>
              <Typography
                sx={{
                  fontFamily: "Georgia, 'Times New Roman', serif",
                  fontSize: {
                    xs: 23,
                    sm: 28,
                    md: 30,
                  },
                  fontWeight: 600,
                  lineHeight: 1.1,
                  color: colors.text,
                }}
              >
                Top Products
              </Typography>

              <Typography
                sx={{
                  fontSize: {
                    xs: 11.5,
                    sm: 13,
                  },
                  color: colors.secondary,
                  mt: 0.5,
                }}
              >
                Curate products featured across your storefront
              </Typography>
            </Box>
          </Box>

          {/* SEARCH */}

          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              gap: 1,
              width: {
                xs: "100%",
                sm: 340,
              },
              height: 42,
              px: 1.4,
              border: `1px solid ${colors.border}`,
              borderRadius: "10px",
              backgroundColor: colors.cardAlt,
              boxShadow: isDark ? "none" : "0 3px 14px rgba(40,30,20,.035)",
            }}
          >
            <SearchRoundedIcon
              sx={{
                fontSize: 19,
                color: colors.secondary,
              }}
            />

            <InputBase
              fullWidth
              placeholder="Search product, brand or category"
              value={searchQuery}
              onChange={(event) => setSearchQuery(event.target.value)}
              sx={{
                fontSize: 12.5,
                color: colors.text,

                "& input::placeholder": {
                  color: colors.secondary,
                  opacity: 1,
                },
              }}
            />

            {searchQuery && (
              <IconButton
                size="small"
                onClick={() => setSearchQuery("")}
                sx={{
                  width: 28,
                  height: 28,
                  color: colors.secondary,

                  "&:hover": {
                    color: colors.accent,
                    backgroundColor: colors.accentSoft,
                  },
                }}
              >
                <Typography
                  sx={{
                    fontSize: 17,
                    lineHeight: 1,
                  }}
                >
                  ×
                </Typography>
              </IconButton>
            )}
          </Box>
        </Box>

        {/* ----------------------------------------------------------------- */}
        {/* STATS */}
        {/* ----------------------------------------------------------------- */}

        <Box
          sx={{
            display: "grid",
            gridTemplateColumns: {
              xs: "1fr 1fr",
              sm: "repeat(3,1fr)",
            },
            gap: 1.5,
            mb: 2.5,
          }}
        >
          {/* TOTAL */}

          <Paper
            elevation={0}
            sx={{
              ...cardSx,
              p: 2,
            }}
          >
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
                gap: 1,
              }}
            >
              <Box>
                <Typography
                  sx={{
                    fontSize: 10.5,
                    color: colors.secondary,
                    letterSpacing: ".06em",
                    fontWeight: 650,
                  }}
                >
                  TOTAL PRODUCTS
                </Typography>

                <Typography
                  sx={{
                    fontSize: 24,
                    fontWeight: 650,
                    color: colors.text,
                    mt: 0.4,
                  }}
                >
                  {rows.length}
                </Typography>
              </Box>

              <Box
                sx={{
                  width: 40,
                  height: 40,
                  borderRadius: "10px",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  backgroundColor: colors.accentSoft,
                  color: colors.accent,
                  flexShrink: 0,
                }}
              >
                <Inventory2OutlinedIcon sx={{ fontSize: 20 }} />
              </Box>
            </Box>
          </Paper>

          {/* FEATURED */}

          <Paper
            elevation={0}
            sx={{
              ...cardSx,
              p: 2,
            }}
          >
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
                gap: 1,
              }}
            >
              <Box>
                <Typography
                  sx={{
                    fontSize: 10.5,
                    color: colors.secondary,
                    letterSpacing: ".06em",
                    fontWeight: 650,
                  }}
                >
                  FEATURED
                </Typography>

                <Typography
                  sx={{
                    fontSize: 24,
                    fontWeight: 650,
                    color: colors.accent,
                    mt: 0.4,
                  }}
                >
                  {topCount}
                </Typography>
              </Box>

              <Box
                sx={{
                  width: 40,
                  height: 40,
                  borderRadius: "10px",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  backgroundColor: colors.accentSoft,
                  color: colors.accent,
                  flexShrink: 0,
                }}
              >
                <StarRoundedIcon sx={{ fontSize: 21 }} />
              </Box>
            </Box>
          </Paper>

          {/* PENDING */}

          <Paper
            elevation={0}
            sx={{
              ...cardSx,
              p: 2,
              gridColumn: {
                xs: "1 / -1",
                sm: "auto",
              },
            }}
          >
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
                gap: 1,
              }}
            >
              <Box>
                <Typography
                  sx={{
                    fontSize: 10.5,
                    color: colors.secondary,
                    letterSpacing: ".06em",
                    fontWeight: 650,
                  }}
                >
                  PENDING CHANGES
                </Typography>

                <Typography
                  sx={{
                    fontSize: 24,
                    fontWeight: 650,
                    color: pendingCount ? colors.accent : colors.text,
                    mt: 0.4,
                  }}
                >
                  {pendingCount}
                </Typography>
              </Box>

              <Box
                sx={{
                  width: 40,
                  height: 40,
                  borderRadius: "10px",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  backgroundColor: pendingCount
                    ? colors.accentSoft
                    : alpha(colors.secondary, 0.08),
                  color: pendingCount ? colors.accent : colors.secondary,
                  flexShrink: 0,
                }}
              >
                <TrendingUpRoundedIcon sx={{ fontSize: 20 }} />
              </Box>
            </Box>
          </Paper>
        </Box>

        {/* ----------------------------------------------------------------- */}
        {/* CATALOGUE */}
        {/* ----------------------------------------------------------------- */}

        <Paper
          elevation={0}
          sx={{
            ...cardSx,
            overflow: "hidden",
          }}
        >
          {/* TABLE HEADER */}

          <Box
            sx={{
              px: {
                xs: 1.5,
                sm: 2,
              },
              py: 1.5,
              display: "flex",
              alignItems: {
                xs: "flex-start",
                sm: "center",
              },
              justifyContent: "space-between",
              gap: 2,
              borderBottom: `1px solid ${colors.border}`,
              flexDirection: {
                xs: "column",
                sm: "row",
              },
            }}
          >
            <Box>
              <Typography
                sx={{
                  fontFamily: "Georgia, 'Times New Roman', serif",
                  fontSize: 18,
                  fontWeight: 600,
                  color: colors.text,
                }}
              >
                Featured Catalogue
              </Typography>

              <Typography
                sx={{
                  fontSize: 11,
                  color: colors.secondary,
                  mt: 0.3,
                }}
              >
                {filteredRows.length} product
                {filteredRows.length !== 1 ? "s" : ""} shown
              </Typography>
            </Box>

            <Tooltip title="Refresh products">
              <span>
                <IconButton
                  onClick={() => fetchData(true)}
                  disabled={loading || refreshing || saving}
                  sx={{
                    width: 36,
                    height: 36,
                    border: `1px solid ${colors.border}`,
                    borderRadius: "9px",
                    color: colors.secondary,

                    "&:hover": {
                      backgroundColor: colors.accentSoft,
                      color: colors.accent,
                      borderColor: colors.accent,
                    },
                  }}
                >
                  <RefreshRoundedIcon
                    sx={{
                      fontSize: 18,
                    }}
                  />
                </IconButton>
              </span>
            </Tooltip>
          </Box>

          {/* REFRESH LINE */}

          {refreshing && (
            <Box
              sx={{
                height: 2,
                backgroundColor: colors.accentSoft,
                overflow: "hidden",
              }}
            >
              <Box
                sx={{
                  width: "35%",
                  height: "100%",
                  backgroundColor: colors.accent,
                  animation: "topProductRefresh 1s ease-in-out infinite",
                  "@keyframes topProductRefresh": {
                    "0%": {
                      transform: "translateX(-120%)",
                    },
                    "100%": {
                      transform: "translateX(400%)",
                    },
                  },
                }}
              />
            </Box>
          )}

          {/* DATA GRID */}

          <Box
            sx={{
              height: {
                xs: 560,
                md: "calc(100vh - 385px)",
              },
              minHeight: 480,
            }}
          >
            {loading ? (
              renderSkeleton()
            ) : (
              <DataGrid
                rows={filteredRows}
                columns={columns}
                disableRowSelectionOnClick
                disableColumnMenu={false}
                pageSizeOptions={[10, 25, 50]}
                initialState={{
                  pagination: {
                    paginationModel: {
                      pageSize: 10,
                      page: 0,
                    },
                  },
                }}
                rowHeight={62}
                columnHeaderHeight={48}
                sx={{
                  border: 0,
                  color: colors.text,

                  // HEADER

                  "& .MuiDataGrid-columnHeaders": {
                    backgroundColor: colors.cardAlt,
                    borderBottom: `1px solid ${colors.border}`,
                  },

                  "& .MuiDataGrid-columnHeader": {
                    outline: "none !important",
                  },

                  "& .MuiDataGrid-columnHeaderTitle": {
                    fontSize: 9.5,
                    fontWeight: 700,
                    letterSpacing: ".08em",
                    color: colors.secondary,
                  },

                  // CELLS

                  "& .MuiDataGrid-cell": {
                    borderBottom: `1px solid ${colors.border}`,
                    outline: "none !important",
                    color: colors.text,
                  },

                  // ROWS

                  "& .MuiDataGrid-row": {
                    transition: "background-color .18s ease",

                    "&:hover": {
                      backgroundColor: colors.accentSoft,
                    },

                    "&.Mui-selected": {
                      backgroundColor: colors.accentSoft,

                      "&:hover": {
                        backgroundColor: colors.accentSoft,
                      },
                    },
                  },

                  // FOOTER

                  "& .MuiDataGrid-footerContainer": {
                    borderTop: `1px solid ${colors.border}`,
                    backgroundColor: colors.card,
                    minHeight: 48,
                  },

                  "& .MuiTablePagination-root": {
                    color: colors.secondary,
                  },

                  "& .MuiTablePagination-select": {
                    color: colors.text,
                  },

                  "& .MuiTablePagination-selectIcon": {
                    color: colors.secondary,
                  },

                  // ICONS

                  "& .MuiDataGrid-iconButtonContainer": {
                    color: colors.secondary,
                  },

                  "& .MuiDataGrid-menuIcon": {
                    color: colors.secondary,
                  },

                  "& .MuiDataGrid-sortIcon": {
                    color: colors.accent,
                  },

                  // CHECKBOX

                  "& .MuiCheckbox-root": {
                    color: colors.secondary,

                    "&.Mui-checked": {
                      color: colors.accent,
                    },
                  },

                  // SCROLLBAR

                  "& ::-webkit-scrollbar": {
                    width: 7,
                    height: 7,
                  },

                  "& ::-webkit-scrollbar-track": {
                    background: colors.cardAlt,
                  },

                  "& ::-webkit-scrollbar-thumb": {
                    backgroundColor: isDark ? "#4B4742" : "#C9C2B9",
                    borderRadius: 10,
                  },

                  "& ::-webkit-scrollbar-thumb:hover": {
                    backgroundColor: colors.accent,
                  },
                }}
              />
            )}
          </Box>
        </Paper>

        {/* ----------------------------------------------------------------- */}
        {/* SAVE BAR */}
        {/* ----------------------------------------------------------------- */}

        <Box
          sx={{
            mt: 2,
            px: {
              xs: 0,
              sm: 0.5,
            },
            display: "flex",
            justifyContent: "flex-end",
            alignItems: "center",
            gap: 1.5,
            flexDirection: {
              xs: "column-reverse",
              sm: "row",
            },
          }}
        >
          {pendingCount > 0 && (
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                gap: 0.8,
                mr: {
                  sm: 0.5,
                },
              }}
            >
              <Box
                sx={{
                  width: 7,
                  height: 7,
                  borderRadius: "50%",
                  backgroundColor: colors.accent,
                  boxShadow: `0 0 0 4px ${colors.accentSoft}`,
                }}
              />

              <Typography
                sx={{
                  fontSize: 11.5,
                  color: colors.secondary,
                }}
              >
                {pendingCount} unsaved change
                {pendingCount !== 1 ? "s" : ""}
              </Typography>
            </Box>
          )}

          <Button
            variant="contained"
            startIcon={<SaveRoundedIcon sx={{ fontSize: 18 }} />}
            disabled={!pendingCount || saving}
            onClick={handleSubmitChanges}
            fullWidth={isMobile}
            sx={{
              minWidth: {
                xs: "100%",
                sm: 160,
              },
              height: 42,
              borderRadius: "9px",
              textTransform: "none",
              fontSize: 12,
              fontWeight: 650,
              backgroundColor: colors.text,
              color: isDark ? "#151412" : "#FFFFFF",
              boxShadow: "none",

              "&:hover": {
                backgroundColor: colors.accent,
                boxShadow: "none",
              },

              "&.Mui-disabled": {
                backgroundColor: isDark ? "rgba(255,255,255,.08)" : "#E4E0DB",
                color: colors.secondary,
              },
            }}
          >
            {saving ? "Saving..." : "Save Changes"}
          </Button>
        </Box>

        {/* ----------------------------------------------------------------- */}
        {/* EMPTY SEARCH RESULT */}
        {/* ----------------------------------------------------------------- */}

        {!loading && rows.length > 0 && filteredRows.length === 0 && (
          <Paper
            elevation={0}
            sx={{
              ...cardSx,
              mt: 2,
              py: 5,
              px: 3,
              textAlign: "center",
            }}
          >
            <Box
              sx={{
                width: 54,
                height: 54,
                borderRadius: "13px",
                mx: "auto",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                backgroundColor: colors.accentSoft,
                color: colors.accent,
                mb: 1.5,
              }}
            >
              <SearchRoundedIcon sx={{ fontSize: 26 }} />
            </Box>

            <Typography
              sx={{
                fontFamily: "Georgia, 'Times New Roman', serif",
                fontSize: 19,
                fontWeight: 600,
                color: colors.text,
              }}
            >
              No products found
            </Typography>

            <Typography
              sx={{
                fontSize: 11.5,
                color: colors.secondary,
                mt: 0.6,
              }}
            >
              Try searching with a different product, brand or category.
            </Typography>

            <Button
              onClick={() => setSearchQuery("")}
              sx={{
                mt: 2,
                borderRadius: "8px",
                color: colors.accent,
                border: `1px solid ${colors.border}`,
                textTransform: "none",
                fontSize: 11.5,

                "&:hover": {
                  borderColor: colors.accent,
                  backgroundColor: colors.accentSoft,
                },
              }}
            >
              Clear Search
            </Button>
          </Paper>
        )}

        {/* ----------------------------------------------------------------- */}
        {/* EMPTY CATALOGUE */}
        {/* ----------------------------------------------------------------- */}

        {!loading && rows.length === 0 && (
          <Paper
            elevation={0}
            sx={{
              ...cardSx,
              mt: 2,
              py: 8,
              px: 3,
              textAlign: "center",
            }}
          >
            <Box
              sx={{
                width: 62,
                height: 62,
                borderRadius: "15px",
                mx: "auto",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                backgroundColor: colors.accentSoft,
                color: colors.accent,
                mb: 2,
              }}
            >
              <CollectionsOutlinedIcon sx={{ fontSize: 30 }} />
            </Box>

            <Typography
              sx={{
                fontFamily: "Georgia, 'Times New Roman', serif",
                fontSize: 20,
                fontWeight: 600,
                color: colors.text,
              }}
            >
              No products available
            </Typography>

            <Typography
              sx={{
                fontSize: 12,
                color: colors.secondary,
                mt: 0.6,
              }}
            >
              Products will appear here when they are available.
            </Typography>
          </Paper>
        )}
      </Box>
    </Box>
  );
};

export default TopProduct;
